// src/pages/RevenueDashboard.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { firestore } from "../firebase";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const toDate = (val) => (val instanceof Date ? val : new Date(val));
const getDaysBetween = (startTime, endTime) => (endTime - startTime) / 86400000;
const TWO_WEEKS = 14 * 86400000;

const ANCHOR_DATE = new Date("2025-01-07T00:00:00"); // first Tuesday of 2025

const getNextPayoutDate = () => {
  const now = new Date();
  let next = new Date(ANCHOR_DATE);

  while (next <= now) {
    next.setDate(next.getDate() + 14);
  }

  next.setHours(0, 0, 0, 0); // Reset to midnight
  return next;
};

const getSundayBefore = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - ((d.getDay() + 1) % 7));
  d.setHours(23, 59, 59, 999);
  return d;
};

const RevenueDashboard = () => {
  const auth = getAuth();
  const [shopSlug, setShopSlug] = useState(null);
  const [user, loadingAuth] = useAuthState(auth);
  const [bookings, setBookings] = useState([]);
  const [visibleCount, setVisibleCount] = useState(6);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("startDate");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterMethod, setFilterMethod] = useState("all");
  const [revenueData, setRevenueData] = useState({
    totalRevenue: 0,
    onlineRevenue: 0,
    manualRevenue: 0,
  });
  const [payoutData, setPayoutData] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const shopId = user ? user.uid : null;

  useEffect(() => {
    const fetchSlug = async () => {
      if (!user) return;

      const ownerRef = doc(firestore, "users", user.uid);
      const ownerSnap = await getDoc(ownerRef);
      if (ownerSnap.exists()) {
        setShopSlug(ownerSnap.data().shopId); // Slug from users
      } else {
        console.error("Shop not found for user.");
      }
    };

    fetchSlug();
  }, [user]);

  useEffect(() => {
    if (!shopSlug) return;
    const fetchData = async () => {
      try {
        const shopRef = doc(firestore, "rentalShops", shopSlug);
        const shopSnap = await getDoc(shopRef);
        const payoutArray = shopSnap.exists()
          ? shopSnap.data().payouts || []
          : [];

        const snapshot = await getDocs(
          collection(firestore, "rentalShops", shopSlug, "bookings")
        );
        const all = snapshot.docs.map((doc) => {
          const data = doc.data();
          const start = toDate(data.startDate).getTime();
          const end = toDate(data.endDate).getTime();
          const days = getDaysBetween(start, end);
          const price = data.bill?.totalCost || 0;
          const method = data.source?.bookingMethod || "manual";
          const title =
            data.label || `${data.vehicle?.vehicleType || "Vehicle"} Booking`;

          return {
            id: doc.id,
            ...data,
            title,
            startTime: start,
            endTime: end,
            fullPrice: price,
            bookingMethod: method,
          };
        });
        setBookings(all);

        const online = all.filter(
          (b) => b.bookingMethod === "online" && b.type !== "blocked"
        );

        const alreadyPaid = payoutArray.reduce(
          (sum, p) => sum + (p.amount || 0),
          0
        );

        const lastPayoutDate =
          payoutArray.length > 0
            ? new Date(payoutArray[0].date)
            : new Date("2025-01-01");
        const nextPayout = getNextPayoutDate(lastPayoutDate);
        const payoutStart = new Date(lastPayoutDate);
        payoutStart.setDate(payoutStart.getDate() - 1);
        const payoutEnd = getSundayBefore(nextPayout);

        let totalOnline = 0;
        let revenueFromBookings = 0;
        let revenueFromInsurance = 0;

        all.forEach((b) => {
          if (b.type === "blocked" || b.bookingMethod !== "online") return;

          const isCancelled = b.status === "cancelled";
          const hasInsurance = b.policySnapshot?.cancellationInsurance;
          const insuranceAmount = b.policySnapshot?.insuranceAmount || 0;
          const cancelledBy = b.cancelledBy;
          const cancellationRefunded = b.cancellationRefunded;
          const amount = b.fullPrice;

          if (!isCancelled) {
            revenueFromBookings += amount;
            if (hasInsurance) {
              revenueFromInsurance += insuranceAmount * 0.5;
            }
          } else {
            if (cancelledBy === "customer") {
              if (hasInsurance) {
                revenueFromInsurance += insuranceAmount * 0.5;
              } else if (!cancellationRefunded) {
                revenueFromBookings += amount;
              }
            }
            // if cancelledBy === "rental shop": revenue = 0
          }
        });

        totalOnline = revenueFromBookings + revenueFromInsurance;
        const amountDue = revenueFromBookings * 0.85 + revenueFromInsurance;
        const amountOwed = amountDue - alreadyPaid;
        const grossTotal = amountOwed / 0.85;
        const platformCommission =
          revenueFromBookings * 0.15 + revenueFromInsurance;

        setPayoutData({
          totalOnlineRevenue: totalOnline,
          amountDueToShop: amountDue,
          alreadyPaid,
          amountOwed: amountDue - alreadyPaid,
          totalRevenueEquivalent: totalOnline,
          platformCommission: platformCommission,
          revenueFromBookings,
          revenueFromInsurance,
          nextPayoutDate: nextPayout,
          payoutStart,
          payoutEnd,
          pastPayouts: payoutArray,
        });
      } catch (err) {
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [shopSlug]);

  useEffect(() => {
    if (bookings.length === 0) return;

    let totalRevenue = 0;
    let onlineRevenue = 0;
    let manualRevenue = 0;

    bookings.forEach((booking) => {
      if (booking.type === "blocked") return;

      const isCancelled = booking.status === "cancelled";
      const hasInsurance = booking.policySnapshot?.cancellationInsurance;
      const insuranceAmount = booking.policySnapshot?.insuranceAmount || 0;
      const cancelledBy = booking.cancelledBy;
      const cancellationRefunded = booking.cancellationRefunded;
      const amount = booking.fullPrice;

      let effectiveRevenue = 0;

      if (!isCancelled) {
        effectiveRevenue = amount;
      } else {
        if (cancelledBy === "customer") {
          if (hasInsurance) {
            // Customer cancelled and had insurance: refund full, keep 50% of insurance
            effectiveRevenue = insuranceAmount * 0.5;
          } else {
            // No insurance
            if (!cancellationRefunded) {
              effectiveRevenue = amount; // shop keeps full
            } // else: fully refunded, revenue = 0
          }
        } else if (cancelledBy === "rental shop") {
          // Full refund no matter what
          effectiveRevenue = 0;
        }
      }

      totalRevenue += effectiveRevenue;
      if (booking.bookingMethod === "manual") {
        manualRevenue += effectiveRevenue;
      } else {
        onlineRevenue += effectiveRevenue;
      }
    });

    setRevenueData({ totalRevenue, onlineRevenue, manualRevenue });
  }, [bookings]);

  const filteredBookings = bookings.filter((booking) => {
    if (booking.type === "blocked") return false;
    const matchesSearch = booking.title
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterMethod === "all" || booking.bookingMethod === filterMethod;
    return matchesSearch && matchesFilter;
  });

  const sortedBookings = filteredBookings.sort((a, b) => {
    const aVal = sortField === "startDate" ? a.startTime : a.fullPrice;
    const bVal = sortField === "startDate" ? b.startTime : b.fullPrice;
    return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
  });

  const displayedBookings = sortedBookings.slice(0, visibleCount);

  if (!user || !shopSlug || loading) {
    return (
      <Container>
        <Typography variant="h6" align="center">
          Loading revenue dashboard...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 2 }}>
      <Typography variant="h4" align="left" gutterBottom>
        Revenue Dashboard
      </Typography>

      {/* Online Revenue Summary */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Online Revenue Summary
        </Typography>
        <Typography>
          <strong>Total Online Revenue:</strong> ₱
          {(payoutData.totalOnlineRevenue || 0).toFixed(2)}
        </Typography>
        <Typography variant="body2">
          From Bookings: ₱{(payoutData.revenueFromBookings || 0).toFixed(2)}
        </Typography>
        <Typography variant="body2">
          From Cancellation Insurance: ₱
          {(payoutData.revenueFromInsurance || 0).toFixed(2)}
        </Typography>

        <Typography>
          <strong>Rental Shop profit:</strong> ₱
          {payoutData.amountDueToShop?.toFixed(2)}
        </Typography>
        <Typography>
          <strong>Paid Out So Far:</strong> ₱
          {payoutData.alreadyPaid?.toFixed(2)}
        </Typography>
        <br></br>
        <Typography>
          <strong>Still Owed: ₱{payoutData.amountOwed?.toFixed(2)}</strong>
        </Typography>

        <Box mt={3}>
          <Typography variant="subtitle1">
            <strong>Next Payout:</strong>{" "}
            {payoutData.nextPayoutDate?.toLocaleDateString()}
          </Typography>
          <Typography>
            Covers bookings from {payoutData.payoutStart?.toLocaleDateString()}{" "}
            to {payoutData.payoutEnd?.toLocaleDateString()}
          </Typography>
        </Box>

        {payoutData.pastPayouts?.length > 0 && (
          <Box mt={3}>
            <Typography variant="subtitle1">Past Payouts</Typography>
            <List>
              {payoutData.pastPayouts.map((p, idx) => (
                <ListItem key={idx}>
                  <ListItemText
                    primary={new Date(p.date).toLocaleDateString()}
                    secondary={`Amount: ₱${p.amount.toFixed(2)}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      {/* Total Revenue Recap */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Total Revenue: ₱{revenueData.totalRevenue.toFixed(2)}
        </Typography>
        <Typography variant="body1">
          Online Bookings: ₱{revenueData.onlineRevenue.toFixed(2)}
        </Typography>
        <Typography variant="body1">
          Manual Bookings: ₱{revenueData.manualRevenue.toFixed(2)}
        </Typography>
      </Paper>

      {/* Booking List */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Bookings List
        </Typography>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Search Bookings"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortField}
                  label="Sort By"
                  onChange={(e) => setSortField(e.target.value)}
                >
                  <MenuItem value="startDate">Start Date</MenuItem>
                  <MenuItem value="fullPrice">Full Price</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Filter By Method</InputLabel>
                <Select
                  value={filterMethod}
                  label="Filter By Method"
                  onChange={(e) => setFilterMethod(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
        {displayedBookings.length > 0 ? (
          <List>
            {displayedBookings.map((booking) => (
              <React.Fragment key={booking.id}>
                <ListItem>
                  <ListItemText
                    primary={booking.title}
                    secondary={
                      <>
                        {`${new Date(
                          booking.startTime
                        ).toLocaleString()} - ${new Date(
                          booking.endTime
                        ).toLocaleString()}`}
                        <br />
                        {booking.status === "cancelled" ? (
                          <>
                            <Typography variant="body2" color="error">
                              Cancelled by: {booking.cancelledBy || "unknown"} |
                              Refunded:{" "}
                              {booking.cancellationRefunded ? "Yes" : "No"}
                            </Typography>
                          </>
                        ) : null}
                        <Typography variant="body2">
                          Revenue Counted: ₱
                          {(() => {
                            const isCancelled = booking.status === "cancelled";
                            const hasInsurance =
                              booking.policySnapshot?.cancellationInsurance;
                            const insuranceAmount =
                              booking.policySnapshot?.insuranceAmount || 0;
                            const cancelledBy = booking.cancelledBy;
                            const cancellationRefunded =
                              booking.cancellationRefunded;
                            const amount = booking.fullPrice;

                            let rev = 0;
                            if (!isCancelled) {
                              rev = amount;
                            } else {
                              if (cancelledBy === "customer") {
                                if (hasInsurance) rev = insuranceAmount * 0.5;
                                else if (!cancellationRefunded) rev = amount;
                              }
                            }
                            return rev.toFixed(2);
                          })()}
                        </Typography>
                      </>
                    }
                  />

                  <Button
                    variant="outlined"
                    onClick={() =>
                      navigate(`/booking/${booking.id}?shopId=${shopSlug}`)
                    }
                  >
                    View Details
                  </Button>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography variant="body2">No bookings found.</Typography>
        )}
        {visibleCount < sortedBookings.length && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Button
              variant="outlined"
              onClick={() => setVisibleCount(visibleCount + 6)}
            >
              Show More
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default RevenueDashboard;
